import { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import FullScreenModal from './FullScreenModal';
import LocationPanel from './LocationPanel';
import { getSuperteams } from '../utils/api';
import { useClickSound } from '../hooks/useClickSound';
import type { Superteam } from '../types';

interface SuperteamsModalProps {
  isOpen: boolean;
  onClose: () => void;
  superteams: Superteam[];
  onSuperteamClick?: (superteam: Superteam) => void;
}

export default function SuperteamsModal({
  isOpen,
  onClose,
  superteams: initialSuperteams,
  onSuperteamClick,
}: SuperteamsModalProps) {
  const { withClickSound } = useClickSound();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSuperteam, setSelectedSuperteam] = useState<Superteam | null>(null);
  const isMobile = window.innerWidth < 768;

  // Fetch superteams data
  const { data: superteamsData } = useQuery({
    queryKey: ['superteams'],
    queryFn: getSuperteams,
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  // Merge API data with initial superteams
  const superteams = useMemo(() => {
    if (!superteamsData?.superteams) return initialSuperteams;
    
    return initialSuperteams.map(staticTeam => {
      const apiTeam = superteamsData.superteams.find(t => t.id === staticTeam.id);
      return {
        ...staticTeam,
        memberCount: apiTeam?.memberCount ?? staticTeam.memberCount,
        activeProjects: apiTeam?.activeProjects ?? staticTeam.activeProjects,
      };
    });
  }, [superteamsData, initialSuperteams]);

  // Filter superteams based on search
  const filteredSuperteams = useMemo(() => {
    if (!searchQuery.trim()) return superteams;
    const query = searchQuery.toLowerCase();
    return superteams.filter(
      team =>
        team.name.toLowerCase().includes(query) ||
        team.country.toLowerCase().includes(query) ||
        team.city?.toLowerCase().includes(query)
    );
  }, [superteams, searchQuery]);

  const handleSuperteamClick = (superteam: Superteam) => {
    setSelectedSuperteam(superteam);
    if (onSuperteamClick) {
      onSuperteamClick(superteam);
    }
  };

  const handleCloseLocationPanel = () => {
    setSelectedSuperteam(null);
  };

  const searchStyle: CSSProperties = {
    padding: isMobile ? '12px 16px' : '16px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.02)',
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'white',
    fontSize: '16px',
    outline: 'none',
  };

  const listStyle: CSSProperties = {
    padding: isMobile ? '16px' : '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const cardStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  return (
    <>
      <FullScreenModal
        isOpen={isOpen && !selectedSuperteam}
        onClose={onClose}
        title="Superteams"
      >
        <div style={searchStyle}>
          <input
            type="text"
            placeholder="Search superteams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={listStyle}>
          {filteredSuperteams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255, 255, 255, 0.5)' }}>
              No superteams found
            </div>
          ) : (
            filteredSuperteams.map((team) => (
              <div
                key={team.id}
                onClick={withClickSound(() => handleSuperteamClick(team))}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(20, 241, 149, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                {team.iconUrl && (
                  <img
                    src={team.iconUrl}
                    alt={team.name}
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '12px',
                      objectFit: 'cover',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'white', margin: '0 0 4px 0' }}>
                    {team.name}
                  </h3>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    {team.city}, {team.country}
                  </p>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                    <span>👥 {team.memberCount}</span>
                    <span>🚀 {team.activeProjects} projects</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </FullScreenModal>

      {selectedSuperteam && (
        <LocationPanel
          superteam={selectedSuperteam}
          superteams={superteams}
          isOpen={true}
          onClose={handleCloseLocationPanel}
          onNavigate={(superteam) => {
            setSelectedSuperteam(superteam);
          }}
          user={null}
          isAuthenticated={false}
          onJoin={async () => {}}
          onLeave={async () => {}}
          joinLoading={false}
        />
      )}
    </>
  );
}
